// src/ts/components/Header/FullHeader.tsx
/* eslint-disable react/jsx-no-bind */
import React, { useRef, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { NextUIProvider } from "@nextui-org/react";

import { oxygenTtamayoContext } from '../../context';
import { theme } from '../../nextui-theme';
import { globalStyles } from '../../global-styles';

import { MobileSidebar } from './MobileSidebar';
import { SearchModal } from './SearchModal';
import { DoubleNavbar } from './DoubleNavbar';

export const FullHeader = () => {
  const [mobileSidebarIsOpen, setMobileSidebarIsOpen] = React.useState<boolean>(false);
  const [navbarIsSticky, setNavbarIsSticky] = React.useState<boolean | null>(null);
  const [searchIsOpen, setSearchIsOpen] = React.useState<boolean>(false);
  const [searchValue, setSearchValue] = React.useState<string>("");
  const toggleRef = useRef<HTMLButtonElement>(null);
  const isMobile = useMediaQuery({ query: '(max-width: 650px)' });
  const isTablet = useMediaQuery({ query: '(min-width: 651px) and (max-width: 960px)' });

  // Gestionar la visibilidad del sidebar y actualizar clases en el HTML
  useEffect(() => {
    // Añadir/quitar clase al elemento html para controlar el sidebar
    if (mobileSidebarIsOpen) {
      document.documentElement.classList.add('sidebar-visible');
    } else {
      document.documentElement.classList.remove('sidebar-visible');
    }

    // Función para cerrar el sidebar al hacer clic fuera
    const handleOutsideClick = (event: MouseEvent) => {
      const sidebarElement = document.querySelector('.z-9000');
      if (mobileSidebarIsOpen && sidebarElement && !sidebarElement.contains(event.target as Node)) {
        setMobileSidebarIsOpen(false);
      }
    };

    // Función para cerrar con la tecla Escape
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (mobileSidebarIsOpen && event.key === 'Escape') {
        setMobileSidebarIsOpen(false);
      }
    };

    // Agregar/quitar event listeners según el estado del sidebar
    if (mobileSidebarIsOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleEscapeKey);
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [mobileSidebarIsOpen]);

  const toggleSearch = (): void => {
    setSearchIsOpen(!searchIsOpen);
  }

  globalStyles();

  // Modificamos la función toggleSidebar para que controle correctamente el estado
  const toggleSidebar = () => {
    setMobileSidebarIsOpen(prev => !prev);
  }

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(evt.target.value);
  }

  return (
    <NextUIProvider theme={theme} disableBaseline>
      <oxygenTtamayoContext.Provider value={window.oxygenTtamayo}>
        <DoubleNavbar
          handleSearchChange={handleSearchChange}
          navbarIsSticky={navbarIsSticky}
          setNavbarIsSticky={setNavbarIsSticky}
          searchValue={searchValue}
          setMobileSidebarIsOpen={setMobileSidebarIsOpen}
          toggleSearch={toggleSearch}
          toggleRef={toggleRef}
          isMobile={isMobile}
          isTablet={isTablet}
          mobileSidebarIsOpen={mobileSidebarIsOpen}
        />
        <MobileSidebar
          sidebarIsOpen={mobileSidebarIsOpen}
          toggleSidebar={toggleSidebar}
          isMobile={isMobile}
          isTablet={isTablet}
        />
        <SearchModal
          handleSearchChange={handleSearchChange}
          setSearchIsOpen={setSearchIsOpen}
          searchIsOpen={searchIsOpen}
          searchValue={searchValue}
        />
      </oxygenTtamayoContext.Provider>
    </NextUIProvider>
  );
}
